import React, { useEffect, useRef, useState } from "react";
import { Icon, Field, PrimaryButton } from "@/components/kit";
import { api } from "@/api";

const CATS = [
  { key: "flooring", label: "Revêtements de sol" },
  { key: "wallpaper", label: "Papiers peints" },
  { key: "painting", label: "Peinture" },
  { key: "curtains", label: "Tentures / voilages / stores" },
];
const catLabel = (k) => (CATS.find((c) => c.key === k) || {}).label || k;

const COUNTRIES = [
  { key: "BE", label: "Belgique (+32)" },
  { key: "FR", label: "France (+33)" },
  { key: "NL", label: "Pays-Bas (+31)" },
  { key: "DE", label: "Allemagne (+49)" },
  { key: "CH", label: "Suisse (+41)" },
  { key: "RO", label: "Roumanie (+40)" },
];
const countryLabel = (k) => (k === "ALL" ? "Tous les pays" : (COUNTRIES.find((c) => c.key === k) || {}).label || k);
const emptyBlock = () => ({ negotiated_price: "", amount_received: false, start_date: "", end_date: "" });

// État pays/tarification à partir d'un partenaire (rétrocompat : ancien prix unique = bloc « Tous les pays »).
const initCountryState = (p) => {
  const cs = p.countries || [];
  const cp = p.country_pricing || {};
  if (Object.keys(cp).length) {
    const all = cs.length === 0;
    return { allCountries: all, countries: cs, pricing: cp };
  }
  const legacy = p.negotiated_price || p.start_date || p.end_date || p.amount_received;
  if (legacy) {
    return { allCountries: true, countries: [], pricing: { ALL: { negotiated_price: p.negotiated_price != null ? String(p.negotiated_price) : "", amount_received: !!p.amount_received, start_date: p.start_date || "", end_date: p.end_date || "" } } };
  }
  return { allCountries: cs.length === 0, countries: cs, pricing: {} };
};

const CategoryChips = ({ selected, onToggle, idPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {CATS.map((c) => {
      const on = selected.includes(c.key);
      return (
        <button key={c.key} type="button" data-testid={`${idPrefix}-cat-${c.key}`} onClick={() => onToggle(c.key)}
          className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${on ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-2 hover:bg-paper-3"}`}>
          {c.label}
        </button>
      );
    })}
  </div>
);

// Section « Pays d'affichage & tarification par pays » (abonnements indépendants par pays).
const CountryPricing = ({ idPrefix, state, onChange }) => {
  const { allCountries, countries, pricing } = state;
  const keys = allCountries ? ["ALL"] : countries;
  const setBlock = (code, field, val) =>
    onChange({ ...state, pricing: { ...pricing, [code]: { ...(pricing[code] || emptyBlock()), [field]: val } } });
  const toggleCountry = (code) => {
    const has = countries.includes(code);
    const next = has ? countries.filter((c) => c !== code) : [...countries, code];
    const np = { ...pricing };
    if (has) delete np[code]; else np[code] = np[code] || emptyBlock();
    onChange({ ...state, countries: next, pricing: np });
  };
  const toggleAll = () => {
    const na = !allCountries;
    onChange({
      allCountries: na,
      countries: na ? [] : countries,
      pricing: na ? { ALL: pricing.ALL || emptyBlock() } : (() => { const np = { ...pricing }; delete np.ALL; return np; })(),
    });
  };
  return (
    <div className="mt-3 rounded-xl border border-line bg-paper-2 p-3">
      <label className="mb-1.5 block text-[13px] font-semibold text-ink">{"Pays d'affichage & tarification"}</label>
      <label className="mb-2 flex items-center gap-2 text-[14px] text-ink-2">
        <input data-testid={`${idPrefix}-allcountries`} type="checkbox" checked={allCountries} onChange={toggleAll} />
        Tous les pays
      </label>
      {!allCountries && (
        <div className="mb-2 flex flex-wrap gap-2">
          {COUNTRIES.map((c) => {
            const on = countries.includes(c.key);
            return (
              <button key={c.key} type="button" data-testid={`${idPrefix}-country-${c.key}`} onClick={() => toggleCountry(c.key)}
                className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${on ? "border-brand bg-brand text-white" : "border-line bg-white text-ink-2 hover:bg-paper-3"}`}>
                {c.label}
              </button>
            );
          })}
        </div>
      )}
      {keys.length === 0 ? (
        <p className="text-[12px] text-mute">Sélectionnez au moins un pays pour définir sa tarification.</p>
      ) : (
        <div className="space-y-2">
          {keys.map((code) => {
            const b = pricing[code] || emptyBlock();
            return (
              <div key={code} data-testid={`${idPrefix}-price-block-${code}`} className="rounded-lg border border-line bg-white p-3">
                <p className="mb-2 text-[13px] font-semibold text-brand">{countryLabel(code)}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Prix de vente négocié (€)" testId={`${idPrefix}-price-${code}`} inputMode="decimal" className="mb-0"
                    value={b.negotiated_price ?? ""} onChange={(e) => setBlock(code, "negotiated_price", e.target.value)} placeholder="0" />
                  <label className="flex items-end gap-2 pb-2 text-[14px] text-ink-2">
                    <input data-testid={`${idPrefix}-received-${code}`} type="checkbox" checked={!!b.amount_received}
                      onChange={(e) => setBlock(code, "amount_received", e.target.checked)} />
                    Montant perçu
                  </label>
                  <div>
                    <label className="mb-1 block text-[13px] font-medium text-ink-3">Début</label>
                    <input type="date" data-testid={`${idPrefix}-start-${code}`} value={b.start_date || ""} onChange={(e) => setBlock(code, "start_date", e.target.value)}
                      className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-[15px] text-ink outline-none focus:border-brand" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[13px] font-medium text-ink-3">Fin</label>
                    <input type="date" data-testid={`${idPrefix}-end-${code}`} value={b.end_date || ""} onChange={(e) => setBlock(code, "end_date", e.target.value)}
                      className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-[15px] text-ink outline-none focus:border-brand" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PartnerCard = ({ p, onReplace, onRemove, onError }) => {
  const [f, setF] = useState({
    name: p.name || "", tagline: p.tagline || "", contact_person: p.contact_person || "", email: p.email || "", phone: p.phone || "",
  });
  const [cats, setCats] = useState(p.categories || []);
  const [cstate, setCstate] = useState(initCountryState(p));
  const replaceRef = useRef(null);

  const save = async (key, value) => {
    if (String(p[key] ?? "") === String(value)) return;
    try { onReplace(await api.updatePartner(p.id, { [key]: value })); }
    catch (e) { onError(e.message); }
  };
  const toggleCat = async (k) => {
    const next = cats.includes(k) ? cats.filter((x) => x !== k) : [...cats, k];
    setCats(next);
    try { onReplace(await api.updatePartner(p.id, { categories: next })); }
    catch (e) { onError(e.message); }
  };
  const saveCountries = async (next) => {
    setCstate(next);
    try {
      onReplace(await api.updatePartner(p.id, {
        countries: next.allCountries ? [] : next.countries,
        country_pricing: next.allCountries ? { ALL: next.pricing.ALL || emptyBlock() } : next.pricing,
      }));
    } catch (e) { onError(e.message); }
  };
  const toggleActive = async () => {
    try { onReplace(await api.updatePartner(p.id, { active: !p.active })); }
    catch (e) { onError(e.message); }
  };
  const replaceLogo = async (file) => {
    if (!file) return;
    try { onReplace(await api.updatePartnerLogo(p.id, file)); }
    catch (e) { onError(e.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer le partenaire « ${p.name} » ?`)) return;
    try { await api.deletePartner(p.id); onRemove(p.id); }
    catch (e) { onError(e.message); }
  };

  return (
    <div data-testid={`partner-row-${p.id}`} className="rounded-2xl border border-line bg-white p-4">
      <div className="flex flex-wrap items-center gap-4">
        <img src={p.logo_url} alt={p.name} className="h-12 w-auto max-w-[120px] object-contain" />
        <div className="flex-1" />
        <button data-testid={`partner-toggle-${p.id}`} onClick={toggleActive}
          className={`rounded-full px-4 py-2 text-[13px] font-semibold ${p.active ? "bg-brand text-white" : "bg-paper-3 text-ink-2"}`}>
          {p.active ? "Actif" : "Inactif"}
        </button>
        <input ref={replaceRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => replaceLogo(e.target.files?.[0] || null)} />
        <button data-testid={`partner-replace-${p.id}`} onClick={() => replaceRef.current?.click()}
          className="rounded-full bg-paper-3 px-4 py-2 text-[13px] font-medium text-ink-2">Changer le logo</button>
        <button data-testid={`partner-delete-${p.id}`} onClick={remove}
          className="flex items-center gap-1 rounded-full px-3 py-2 text-[13px] font-medium text-err">
          <Icon name="trash-2" size={15} /> Supprimer
        </button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field label="Nom de l'entreprise" testId={`partner-name-${p.id}`} value={f.name} className="mb-0"
          onChange={(e) => setF({ ...f, name: e.target.value })} onBlur={() => save("name", f.name)} />
        <Field label="Phrase sous le logo (encart)" testId={`partner-tagline-${p.id}`} value={f.tagline} className="mb-0"
          onChange={(e) => setF({ ...f, tagline: e.target.value })} onBlur={() => save("tagline", f.tagline)} placeholder="Ex : Votre partenaire déco (accessoire)" />
        <Field label="Personne de contact" testId={`partner-contact-${p.id}`} value={f.contact_person} className="mb-0"
          onChange={(e) => setF({ ...f, contact_person: e.target.value })} onBlur={() => save("contact_person", f.contact_person)} />
        <Field label="Email" testId={`partner-email-${p.id}`} value={f.email} className="mb-0"
          onChange={(e) => setF({ ...f, email: e.target.value })} onBlur={() => save("email", f.email)} />
        <Field label="Téléphone" testId={`partner-phone-${p.id}`} value={f.phone} className="mb-0"
          onChange={(e) => setF({ ...f, phone: e.target.value })} onBlur={() => save("phone", f.phone)} />
      </div>
      <div className="mt-3">
        <label className="mb-1.5 block text-[13px] font-medium text-ink-3">Catégories (où le logo apparaît)</label>
        <CategoryChips selected={cats} onToggle={toggleCat} idPrefix={`partner-${p.id}`} />
      </div>
      <CountryPricing idPrefix={`partner-${p.id}`} state={cstate} onChange={saveCountries} />
    </div>
  );
};

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", tagline: "", contact_person: "", email: "", phone: "", active: true });
  const [cats, setCats] = useState([]);
  const [cstate, setCstate] = useState({ allCountries: true, countries: [], pricing: { ALL: emptyBlock() } });
  const [file, setFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const addInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setPartners(await api.listPartners()); setError(""); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addPartner = async () => {
    if (!form.name.trim() || !file) { setError("Nom de l'entreprise et logo requis."); return; }
    setAdding(true); setError("");
    try {
      const countries = cstate.allCountries ? [] : cstate.countries;
      const country_pricing = cstate.allCountries ? { ALL: cstate.pricing.ALL || emptyBlock() } : cstate.pricing;
      await api.createPartner({ ...form, name: form.name.trim(), categories: cats, countries, country_pricing }, file);
      setForm({ name: "", tagline: "", contact_person: "", email: "", phone: "", active: true });
      setCats([]); setCstate({ allCountries: true, countries: [], pricing: { ALL: emptyBlock() } }); setFile(null);
      if (addInputRef.current) addInputRef.current.value = "";
      await load();
    } catch (e) { setError(e.message); } finally { setAdding(false); }
  };

  const replaceP = (u) => setPartners((ps) => ps.map((x) => (x.id === u.id ? u : x)));
  const removeP = (id) => setPartners((ps) => ps.filter((x) => x.id !== id));

  return (
    <div className="mx-auto max-w-4xl px-5 py-8">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-semibold text-ink">Partenaires recommandés</h1>
        <p className="mt-1 text-[14px] text-mute">
          {"CRM partenaires. Les logos actifs apparaissent sur la page de résultats et les documents UNIQUEMENT dans leur(s) catégorie(s) ET pour les pays d'affichage choisis (selon l'indicatif téléphonique du prospect). La tarification est gérée par pays (abonnements indépendants). Ils n'influencent jamais le calcul de l'estimation."}
        </p>
      </div>

      {error ? <p data-testid="partners-error" className="mb-4 text-[13px] text-err">{error}</p> : null}

      <div className="mb-6 rounded-2xl border border-line bg-white p-5" data-testid="partner-add-panel">
        <h3 className="mb-3 text-[16px] font-semibold text-ink">Ajouter un partenaire</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nom de l'entreprise" testId="partner-name" value={form.name} className="mb-0"
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Farrow & Ball" />
          <Field label="Phrase sous le logo (encart)" testId="partner-tagline" value={form.tagline} className="mb-0"
            onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Ex : Votre partenaire déco (accessoire)" />
          <Field label="Personne de contact" testId="partner-contact" value={form.contact_person} className="mb-0"
            onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="Ex : Marie Dupont" />
          <Field label="Email" testId="partner-email" type="email" value={form.email} className="mb-0"
            onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@marque.com" />
          <Field label="Téléphone" testId="partner-phone" value={form.phone} className="mb-0"
            onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+32 ..." />
          <div>
            <label className="mb-1 block text-[13px] font-medium text-ink-3">Logo (png/jpg/webp)</label>
            <input ref={addInputRef} data-testid="partner-file" type="file" accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-[13px] text-ink-2" />
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1.5 block text-[13px] font-medium text-ink-3">Catégories</label>
          <CategoryChips selected={cats} onToggle={(k) => setCats(cats.includes(k) ? cats.filter((x) => x !== k) : [...cats, k])} idPrefix="new" />
        </div>
        <CountryPricing idPrefix="new" state={cstate} onChange={setCstate} />
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
          <label className="flex items-center gap-2 text-[14px] text-ink-2">
            <input data-testid="partner-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Actif (visible sur la page de résultats)
          </label>
        </div>
        <div className="mt-3"><PrimaryButton testId="partner-add" title={adding ? "Ajout…" : "Ajouter le partenaire"} icon="plus" onClick={addPartner} /></div>
      </div>

      {loading ? (
        <p className="text-[14px] text-mute">Chargement…</p>
      ) : partners.length === 0 ? (
        <p data-testid="partners-empty" className="text-[14px] text-mute">Aucun partenaire pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {partners.map((p) => (
            <PartnerCard key={p.id} p={p} onReplace={replaceP} onRemove={removeP} onError={setError} />
          ))}
        </div>
      )}
    </div>
  );
}

export { catLabel };
